/**
 * CalendarCard — the calendar card for a review unit on the shared
 * pipeline (CLAUDE.md §34.9 / §34.10). Third card type alongside the
 * file FolderCard (manila folder) and PrepSheetCard (cream notebook): a
 * ring-bound desk calendar leaf in the app's teal/orange, reading as
 * unmistakably "an appointment" at a glance.
 *
 * GEOMETRY (CLAUDE.md §34.9) — this card matches the FOOTPRINT of the
 * other two pipeline cards; only the artwork inside differs:
 *   - it starts at the same y in a column, by importing the SAME
 *     CARD_TOP_CLEARANCE value FolderCard exports and PrepSheetCard
 *     already consumes (one source of truth, nothing duplicated);
 *   - it carries the same minimum body height as a prep sheet, so a
 *     calendar card sits in the same size band as its neighbours
 *     instead of towering over them;
 *   - the artwork is FITTED to a fixed artwork area via
 *     preserveAspectRatio="xMidYMin meet" — the leaf scales down into
 *     the box and anchors to the TOP of that area, so any slack falls
 *     at the bottom where it reads as ordinary card padding. The card
 *     is never resized to suit the artwork.
 * The SVG viewBox is tight to the true bounding box of the drawing
 * (rings included), so there is no dead space above the leaf pushing it
 * visually down inside an otherwise aligned box.
 *
 * The card is a VIEW of review_clients rows — no separate task table.
 * One card renders per UNIT: a whole group (whose members share status
 * and timestamps by invariant) renders a single card with the group
 * variant artwork — two offset leaf-pages stacked behind the leaf and
 * an orange member-count badge; an ungrouped client renders the plain
 * leaf.
 *
 * Visibility: ALL roles see calendar cards; only the Adviser can open
 * one. The `interactive` prop is the same app-level gate used by
 * FolderCard / PrepSheetCard (§30).
 *
 * Calendar cards never participate in file concurrency gating, SLA
 * badges, stats, or the bonus counter (§34.7).
 */

import React from 'react';
import { monthAbbrev, MONTH_NAMES, REVIEW_STATUS_LABELS } from '../../utils/reviewPlanner';
// Imported, never re-typed: the same top clearance FolderCard defines and
// PrepSheetCard consumes, so all three card types begin at the same y.
import { CARD_TOP_CLEARANCE } from './FolderCard';

const TEAL = 'var(--cp-secondary)';
const TEAL_SOFT = 'rgba(27, 154, 170, 0.18)';
const ORANGE = 'var(--cp-accent)';
const INK = '#1A2B3C';

// Height of the artwork area inside the card body. Sized as large as the
// card's fixed height allows: the three text rows below are set to tight
// line-heights and margins precisely so this area can be bigger, keeping
// the card in the same band as a FolderCard / PrepSheetCard (measured,
// see §34.9). The artwork is fitted into this area, never the other way
// round — the card is never grown to suit the drawing.
const ART_AREA_HEIGHT = '37px';

// Tight viewBoxes — the true bounding box of each variant's drawing,
// rings and all. No padding above the artwork, so "top of the box" and
// "top of the drawing" are the same line.
const VIEWBOX_SINGLE = '15 9 92 96';
const VIEWBOX_GROUP = '15 9 98 104';

/**
 * The ring-bound desk calendar leaf. The header band carries the unit's
 * REVIEW MONTH abbreviation; the numeral is purely decorative (the
 * day-of-month the card was submitted). Internal proportions are tuned
 * for the small rendered size: a proportionally taller header band,
 * larger month/numeral type and thicker strokes than a full-size
 * drawing would need, so the month still reads at card scale.
 * The `group` variant stacks two offset pages behind the leaf and adds
 * an orange member-count badge.
 */
function CalendarLeaf({ month, dayNumber, group = false, memberCount = 1 }) {
  return (
    <svg
      viewBox={group ? VIEWBOX_GROUP : VIEWBOX_SINGLE}
      // Scale down to fit the artwork area and anchor to its TOP; slack
      // falls at the bottom, never above the drawing.
      preserveAspectRatio="xMidYMin meet"
      width="100%"
      height="100%"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      {group && (
        <>
          {/* Offset leaf-pages stacked behind the front leaf. The offset
              is kept small so the stack stays inside the same box. */}
          <path
            d="M27 24 a7 7 0 0 1 7-7 h72 a7 7 0 0 1 7 7 v62 a7 7 0 0 1 -7 7 h-4 V33 a13 13 0 0 0 -13-13 H27 Z"
            fill="#fff" stroke={TEAL} strokeWidth="3.5" opacity="0.45"
          />
          <path
            d="M22 29 a7 7 0 0 1 7-7 h72 a7 7 0 0 1 7 7 v62 a7 7 0 0 1 -7 7 h-1 V39 a13 13 0 0 0 -13-13 H22 Z"
            fill="#fff" stroke={TEAL} strokeWidth="3.5" opacity="0.7"
          />
        </>
      )}
      {/* Page with folded bottom-left corner */}
      <path
        d="M18 33 a7 7 0 0 1 7-7 h72 a7 7 0 0 1 7 7 v62 a7 7 0 0 1 -7 7 H48 L18 84 Z"
        fill="#fff" stroke={TEAL} strokeWidth="5.5"
      />
      <path
        d="M18 84 L48 102 L48 84 Z"
        fill={TEAL_SOFT} stroke={TEAL} strokeWidth="5.5" strokeLinejoin="round"
      />
      {/* Teal header band — proportionally tall so the month abbreviation
          stays legible once the leaf is scaled into the artwork area. */}
      <path d="M18 33 a7 7 0 0 1 7-7 h72 a7 7 0 0 1 7 7 v34 H18 Z" fill={TEAL} />
      <text
        x="61" y="54" textAnchor="middle" fontWeight="800" fontSize="27"
        fill="#fff" letterSpacing="1" fontFamily="'Montserrat', 'DM Sans', sans-serif"
      >
        {month}
      </text>
      {/* Binder rings */}
      <circle cx="38" cy="26" r="5" fill="none" stroke={TEAL} strokeWidth="5.5" />
      <circle cx="84" cy="26" r="5" fill="none" stroke={TEAL} strokeWidth="5.5" />
      <line x1="38" y1="26" x2="38" y2="12" stroke={TEAL} strokeWidth="5.5" strokeLinecap="round" />
      <line x1="84" y1="26" x2="84" y2="12" stroke={TEAL} strokeWidth="5.5" strokeLinecap="round" />
      {/* Decorative date numeral with the hand-drawn orange ellipse */}
      <text
        x="61" y="95" textAnchor="middle" fontWeight="800" fontSize="34"
        fill={INK} fontFamily="'Montserrat', 'DM Sans', sans-serif"
      >
        {dayNumber}
      </text>
      <ellipse
        cx="61" cy="85" rx="24" ry="15" fill="none" stroke={ORANGE}
        strokeWidth="5" transform="rotate(-6 61 85)" strokeLinecap="round"
        strokeDasharray="100 8"
      />
      {group && (
        <>
          {/* Member-count badge — orange circle, white ring. */}
          <circle cx="99" cy="99" r="13" fill={ORANGE} stroke="#fff" strokeWidth="3" />
          <text
            x="99" y="105" textAnchor="middle" fontWeight="800" fontSize="17"
            fill="#fff" fontFamily="'Montserrat', 'DM Sans', sans-serif"
          >
            {memberCount}
          </text>
        </>
      )}
    </svg>
  );
}

export default function CalendarCard({ card, onClick, interactive = true }) {
  const anchor = card.members[0];
  const abbrev = monthAbbrev(anchor.reviewMonth);
  // Decorative only — the day the card was submitted (falls back to the
  // review month number when the timestamp is somehow missing).
  const dayNumber = card.submittedAt
    ? new Date(card.submittedAt).getDate()
    : anchor.reviewMonth;

  const statusLabel = (REVIEW_STATUS_LABELS[card.status] || card.status).toUpperCase();
  const chipColours = card.status === 'appointment_made'
    ? { background: 'rgba(244, 162, 97, 0.18)', color: 'var(--cp-accent-dark)' }
    : { background: 'rgba(27, 154, 170, 0.12)', color: 'var(--cp-secondary)' };

  const subtitle = card.isGroup
    ? `${card.members.length} clients · ${MONTH_NAMES[anchor.reviewMonth] || ''} review`
    : `${MONTH_NAMES[anchor.reviewMonth] || ''} review`;

  // Same app-level role gate as FolderCard / PrepSheetCard (§30): a
  // non-permitted role sees the card in full, without button semantics
  // or the pointer cursor.
  const gateBind = interactive
    ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': `Open review calendar card for ${card.label}`,
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick && onClick();
          }
        },
      }
    : {};

  return (
    <div
      onClick={interactive ? onClick : undefined}
      {...gateBind}
      style={{ ...styles.card, cursor: interactive ? 'pointer' : 'default' }}
    >
      <div style={styles.artWrap}>
        <CalendarLeaf
          month={abbrev}
          dayNumber={dayNumber}
          group={card.isGroup}
          memberCount={card.members.length}
        />
      </div>
      {/* Group label, or the natural display name for a single client —
          NOT the surname-first list format, which is a planner list-
          surface convention (§34.2). */}
      <div style={styles.client}>{card.label}</div>
      <div style={styles.subtitle}>{subtitle}</div>
      <span style={{ ...styles.statusChip, ...chipColours }}>{statusLabel}</span>
    </div>
  );
}

const styles = {
  card: {
    position: 'relative',
    // Same top clearance a FolderCard reserves for its folder tab and a
    // PrepSheetCard already imports, so all three card types start at
    // the same y in their column.
    marginTop: CARD_TOP_CLEARANCE,
    // Same minimum body height as a prep sheet, so the calendar card
    // sits in the same size band as its neighbours.
    minHeight: '120px',
    boxSizing: 'border-box',
    background: '#FFFFFF',
    border: '2px solid var(--cp-secondary)',
    borderRadius: '14px',
    padding: '12px',
    textAlign: 'center',
    fontFamily: "'Montserrat', 'DM Sans', sans-serif",
    // Same soft elevation as the folder and notebook cards.
    boxShadow: '0 2px 6px rgba(0,0,0,0.08), 0 8px 18px rgba(0,0,0,0.06)',
  },
  // Fixed artwork area: the leaf is fitted into this box (see
  // preserveAspectRatio above), so the artwork can never drive the
  // card's height.
  artWrap: {
    height: ART_AREA_HEIGHT,
    marginBottom: '3px',
  },
  client: {
    fontSize: '0.9rem',
    fontWeight: 700,
    lineHeight: 1.15,
    color: 'var(--cp-primary)',
    marginBottom: '1px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  subtitle: {
    fontSize: '0.72rem',
    lineHeight: 1.1,
    color: 'var(--cp-text-muted)',
    marginBottom: '3px',
  },
  statusChip: {
    display: 'inline-block',
    padding: '0.05rem 0.6rem',
    borderRadius: '999px',
    fontSize: '0.62rem',
    lineHeight: 1.35,
    fontWeight: 700,
    letterSpacing: '0.05em',
  },
};
